import area from '@turf/area'
import buffer from '@turf/buffer'
import centroid from '@turf/centroid'
import combine from '@turf/combine'
import flip from '@turf/flip'
import { featureCollection } from '@turf/helpers'
import length from '@turf/length'
import { toWgs84 } from '@turf/projection'
import simplify from '@turf/simplify'
import { cloneDeep } from 'lodash'
import get from 'lodash/get'
import isNil from 'lodash/isNil'
import unset from 'lodash/unset'
import OLFormatEsriJSON from 'ol/format/EsriJSON'
import OLFormatGeoJSON from 'ol/format/GeoJSON'
import OLFormatGML3 from 'ol/format/GML3'
import OLFormatWKT from 'ol/format/WKT'

import * as PoziUtils from 'api/utils'
import { GetWhatsHereWKT } from 'drawer/tabs/info/WhatsHereUtils'
import PoziNotifications from 'utils/notifications'
import { convertFeatureCollectionFeaturesToPolygons } from 'utils/TurfUtils'

export default function (olFeature) {
  console.debug('GetGeometryProperties', olFeature)

  if (isNil(olFeature)) {
    console.log('Invalid olFeature object!')
    return {}
  }

  let geomProperties = {}
  try {
    const featureCollectionT = convertOLFeatureToWgs84FeatureCollection(olFeature)
    geomProperties = GetGeometryPropertiesFromTurfFeatureCollection(featureCollectionT, olFeature)
  } catch (err) {
    console.log('GetGeometryProperties: failed unexpectedly', olFeature, err)
  }
  return geomProperties
}

export function GetGeometryPropertiesFromTurfFeatureCollection (featureCollectionTOrig, olFeature = null) {
  // console.debug('GetGeometryPropertiesFromTurfFeatureCollection', featureCollectionTOrig, olFeature)

  // The code below has side effects, potentially changing the original data, hence we work on a clone
  const featureCollectionT = cloneDeep(featureCollectionTOrig)

  // console.debug('GetGeometryPropertiesFromTurfFeatureCollection', featureCollectionT)

  // Check if feature collection has geometry
  if (!featureCollectionHasGeometry(featureCollectionT)) {
    return {}
  }

  // const fcOriginalIsPointsOnly = featureCollectionIsPointsOnly(featureCollectionT)
  // console.debug('ShowWhatsHere - fcOriginalIsPointsOnly', fcOriginalIsPointsOnly)

  const fcGeomType = featureCollectionT.features?.[0]?.geometry?.type
  const fcIsGeometryCollection = PoziUtils.getGeomTypeSimple(fcGeomType) === 'geometrycollection'

  // console.debug('GetGeometryPropertiesFromTurfFeatureCollection - fcIsGeometryCollection?', fcGeomType, fcIsGeometryCollection)

  let featureCollectionAsPolygons
  let olFeaturesAsPolygons
  if (fcIsGeometryCollection) {
    // Convert all features to to polygons
    featureCollectionAsPolygons = convertFeatureCollectionFeaturesToPolygons(featureCollectionT)

    // Combine all features into one multi-polygon
    featureCollectionAsPolygons = combine(featureCollectionAsPolygons)

    // Create OL features
    olFeaturesAsPolygons = getOLFeatures(featureCollectionAsPolygons)
  }

  const cqlgeometrywhatshere = GetWhatsHereWKT()
  const olFeatures = getOLFeatures(featureCollectionT)
  const featureCollectionTFlipped = flip(featureCollectionT)
  const olFeaturesFlipped = getOLFeatures(featureCollectionTFlipped)
  const centroid = getCentroid(featureCollectionT)
  const centroidFCFlipped = flip(featureCollection([centroid]))
  const olFeaturesCentroidFlipped = getOLFeatures(centroidFCFlipped)
  const cqlgeometrysimplified = GetSimplifiedCQL(featureCollectionT)

  /*
  const pointOnSurface = getPointOnSurface(featureCollectionT)
  const pointOnSurfaceFlipped = null
  const olFeaturesPointOnSurfaceFlipped = null
  const cqlgeometrypointonsurface = null
  if (pointOnSurface) {
    pointOnSurfaceFlipped = flip(pointOnSurface)
    olFeaturesPointOnSurfaceFlipped = getOLFeatures(pointOnSurfaceFlipped)
    cqlgeometrypointonsurface = getCQL(olFeaturesPointOnSurfaceFlipped)
  } */

  const ar = area(featureCollectionT) // M^2
  const lngth = length(featureCollectionT) * 1000 // Length is returned in km... convert to meters.

  // NOTE: If you add anything here also add it to defaultExcludeProperties in AddDefaultExcludes.js
  const geomProperties = {
    $cqlgeometrywhatshere: cqlgeometrywhatshere,
    $centroid: centroid,
    $longitude: getLongitude(centroid),
    $latitude: getLatitude(centroid),
    $wkt: getWKT(olFeatures),
    $cqlgeometry: getCQL(olFeaturesFlipped),
    $cqlgeometrysimplified: cqlgeometrysimplified,
    $cqlgeometrycentroid: getCQL(olFeaturesCentroidFlipped),
    $esriGeometry: () => getEsri(olFeaturesAsPolygons || olFeatures), // return a function for delayed execution
    $esriGeometryType: () => getEsriGeomType(featureCollectionAsPolygons || featureCollectionT), // return a function for delayed execution
    $gml: getGml(olFeaturesAsPolygons || olFeatures), // GML cannot handle GeometryCollection type, so we use the MultiPolygon type
    $area: ar,
    $length: lngth,
    $type: fcGeomType
  }

  console.debug('GetGeometryProperties - properties', geomProperties)

  return geomProperties
}

function GetSimplifiedCQL (featureCollectionT) {
  // First combine a FeatureCollection of Point, LineString, or Polygon features into MultiPoint, MultiLineString, or MultiPolygon features
  try {
    let fcCombined = featureCollectionT
    try {
      fcCombined = combine(featureCollectionT)
    } catch (err) {
      console.log('Unable to combine')
      fcCombined = featureCollectionT
    }

    // Then create a small negative buffer around the collection
    let fcBuffered = featureCollectionT
    try {
      const bufferMeters = -1
      fcBuffered = buffer(fcCombined, bufferMeters * 0.001, {
        units: 'kilometers',
        steps: 4
      })

      // If the buffered feature collection is empty then just use the original. This
      // can happen if the original feature collection is a single point or a line string.
      if (!fcBuffered?.features?.length) {
        console.debug('GetSimplifiedCQL - Buffered feature collection is empty. Using original feature collection.')

        fcBuffered = featureCollectionT
      }
    } catch (err) {
      console.log('GetSimplifiedCQL - Unable to buffer. Using original feature collection.')
      fcBuffered = featureCollectionT
    }

    // Then simplify the buffered feature collection
    let fcSimplified
    try {
      fcSimplified = simplify(fcBuffered, {
        tolerance: 0.000005,
        highQuality: false,
        mutate: false
      })

      if (!fcSimplified?.features?.length) {
        console.debug('GetSimplifiedCQL - Simplified feature collection is empty. Using original feature collection.')

        fcSimplified = featureCollectionT
      }
    } catch (err) {
      console.log('GetSimplifiedCQL - Unable to simplify. Using original feature collection.')

      fcSimplified = featureCollectionT
    }

    // Finally flip the coordinates of the simplified feature collection
    const fcFlipped = flip(fcSimplified)
    const olFeaturesFlippedAndSimplified = getOLFeatures(fcFlipped)
    const cql = getCQL(olFeaturesFlippedAndSimplified)

    // console.debug('GetSimplifiedCQL - simplified cql', cql)

    return cql
  } catch (err) {
    console.log('Simplify error', err)
  }

  return null
}

function convertOLFeatureToWgs84FeatureCollection (olFeature) {
  let collection = {}
  try {
    const geoJSONFeature = PoziUtils.convertOLFeatureToTurfFeature(olFeature)
    const featureCollectionT = featureCollection([geoJSONFeature])
    collection = toWgs84(featureCollectionT)
  } catch (err) {
    console.error('convertOLFeatureToWgs84FeatureCollection: unexpected error', olFeature)
  }
  return collection
}

/**
 * Simple check for a feature collection with valid geometry
 *
 * @param {GeoJSON} featureCollectionT
 * @returns {bool} Has valid geometry
 */
function featureCollectionHasGeometry (fc) {
  const firstGeom = get(fc, 'features[0].geometry', null)
  if (isNil(firstGeom)) {
    return false
  }
  return true
}

/**
 * Get the longitude of the centroid
 *
 * @param {GeoJSON} featureCollectionT
 * @returns {number} Longitude based on centroid. For a point this will be the same.
 */
function getLongitude (centroid) {
  return centroid.geometry.coordinates[0]
}

/**
 * Get the latitude of the centroid
 *
 * @param {GeoJSON} featureCollectionT
 * @returns {number} Latitude based on centroid. For a point this will be the same.
 */
function getLatitude (centroid) {
  return centroid.geometry.coordinates[1]
}

/**
 * Get the centroid [lng,lat]
 *
 * @param {GeoJSON} featureCollectionT
 * @returns {number} Latitude based on centroid. For a point this will be the same.
 */
function getCentroid (featureCollectionT) {
  return centroid(featureCollectionT)
}

/**
 * Get the centroid [lng,lat]
 *
 * @param {GeoJSON} featureCollectionT
 * @returns {number} Latitude based on centroid. For a point this will be the same.
 */
/*
function getPointOnSurface (featureCollectionT) {
  const buffered = featureCollectionT
  try {
    buffered = buffer(featureCollectionT, -0.0001, { units: 'kilometers' }) || featureCollectionT
  } catch (err) {
    console.log('Unable to buffer feature collection. Using initial featurecollection.')
  }

  try {
    return featureCollection([pointOnFeature(buffered)])
  } catch (err2) {
    console.log('Unable to get pointOnFeature.. returning null')
  }
  return null
} */

function getOLFeatures (featureCollectionT) {
  featureCollectionT.features.forEach(function (feature) {
    unset(feature, 'properties')
  })
  const olGeoJSON = new OLFormatGeoJSON()
  return olGeoJSON.readFeatures(featureCollectionT)
}

function getWKT (olFeatures) {
  // console.debug('getWKT', olFeatures)

  const olFormatWKT = new OLFormatWKT()
  return olFormatWKT.writeFeatures(olFeatures)
}

function getCQL (olFeaturesFlipped) {
  return getWKT(olFeaturesFlipped)
}

function getEsri (olFeatures) {
  const oLFormatEsriJSON = new OLFormatEsriJSON()
  let esriFeatures = null
  let esriGeometry = null
  try {
    // Extract ESRI features
    esriFeatures = (oLFormatEsriJSON.writeFeaturesObject(olFeatures))?.features

    // Only use geometry of first feature as we currently annot handle more than one feature
    esriGeometry = JSON.stringify(esriFeatures[0]?.geometry, null)
  } catch (error) {
    console.error('getEsri: Cannot convert features to ESRI format.', olFeatures)
    console.error('getEsri: Maybe it contains a GeometryCollection which is not supported?')
  }

  // We currently do not support multiple features in ArcGIS
  if (esriFeatures?.length > 1) {
    console.log('getEsri - Only first geometry of first feature is being returned', esriFeatures, esriGeometry)
    PoziNotifications().notify('This service may not return all selected objects. Only showing first object.')
  }

  return esriGeometry
}

/**
 * Find the Esri geometry type based on the first feature.
 *
 * @external http://resources.esri.com/help/9.3/arcgisengine/ArcObjects/esriGeometry/esriGeometryType.htm
 * @param {GeoJSON} featureCollectionT
 *
 */
function getEsriGeomType (featureCollectionT) {
  // esriGeometryNull // 0 A geometry of unknown type.
  // esriGeometryPoint // 1 A single zero dimensional geometry.
  // esriGeometryMultipoint // 2 An ordered collection of points.
  // esriGeometryLine // 13 A straight line segment between two points.
  // esriGeometryCircularArc// 14 A portion of the boundary of a circle.
  // esriGeometryEllipticArc // 16 A portion of the boundary of an ellipse.
  // esriGeometryBezier3Curve// 15 A third degree bezier curve (four control points).
  // esriGeometryPath // 6 A connected sequence of segments.
  // esriGeometryPolyline// 3 An ordered collection of paths.
  // esriGeometryRing // 11 An area bounded by one closed path.
  // esriGeometryPolygon// 4 A collection of rings ordered by their containment relationship.
  // esriGeometryEnvelope // 5 A rectangle indicating the spatial extent of another geometry.
  // esriGeometryAny// 7 Any of the geometry coclass types.
  // esriGeometryBag // 17 A collection of geometries of arbitrary type.
  // esriGeometryMultiPatch // 9 A collection of surface patches.
  // esriGeometryTriangleStrip // 18 A surface patch of triangles defined by three consecutive points.
  // esriGeometryTriangleFan // 19 A surface patch of triangles defined by the first point and two consecutive points.
  // esriGeometryRay // 20 An infinite, one-directional line extending from an origin point.
  // esriGeometrySphere // 21 A complete 3 dimensional sphere.
  // esriGeometryTriangles // 22 A surface patch of triangles defined by non-overlapping sets of three consecutive points each.
  const geomType = get(featureCollectionT, 'features[0].geometry.type', '') // Basic check of first type.
  switch (geomType) {
    case 'Point':
      return 'esriGeometryPoint'
    case 'MultiPoint':
      return 'esriGeometryMultipoint'
    case 'LineString':
    case 'MultiLineString':
      return 'esriGeometryPolyline'
    case 'LinearRing':
      return 'esriGeometryRing'
    case 'Polygon':
      return 'esriGeometryPolygon'
    case 'MultiPolygon':
      return 'esriGeometryPolygon'
    case 'Circle':
      return 'esriGeometryCircularArc'
    default:
      console.error('getEsriGeomType: Geometry type not supported.', geomType)
      return 'esriGeometryNull'
  }
}

function getGml (olFeatures) {
  // console.debug('GetGeometryProperties - getGml', olFeatures)

  const gmlReader = new OLFormatGML3({
    multiCurve: false, // QGIS does not like multiCurve
    multiSurface: false, // QGIS does not like multiSurface

    // QGIS needs a projection, otherwise it will try to filter to the same projection as the datasource without translation.  Obviously,
    // with the GML string being generated in EPSG:4326, this won't translate to other projections (particularly metre based ones).
    // Unfortunately, the standards used by GML dictate the order of coordinates for EPSG:4326 to be (y, x), but QGIS expects (x, y) in its
    // filters.  So if we specify EPSG:4326 (which we should), we won't get any results.  A hack to work around this is to specify another
    // lat/lon system (EPSG:7844) that maintains (x, y) order, then substitute the projection back to EPSG:4326 before passing to QGIS.
    srsName: 'EPSG:7844'
  })

  const xmlSerializer = new XMLSerializer()

  let gmlGeom

  try {
    // Extract the geometry of the first feature
    const olFeature = olFeatures?.[0]
    const olGeom = olFeature?.getGeometry()

    // console.debug('GetGeometryProperties - getGml - olGeom', olGeom, olFeature)

    const gmlGeomNode = gmlReader.writeGeometryNode(olGeom, {
      decimals: 8 // has to be >= 7 for multi-click select to work
    })

    // Convert <geom>....</geom> node content to string
    gmlGeom = xmlSerializer.serializeToString(gmlGeomNode?.firstElementChild)
    gmlGeom = gmlGeom.replace('srsName="EPSG:7844"', 'srsName="EPSG:4326"')

    // console.debug('GetGeometryProperties - getGml - geom', gmlGeomNode, gmlGeom)
  } catch (error) {
    console.log('GetGeometryProperties - getGml - error', error)

    // return a value that can tell the user that something has gone wrong
    gmlGeom = 'gml_error_please_report'
  }

  return gmlGeom
}
